import React from "react";
import { motion } from "framer-motion";
import {
    Zap,
    Layers,
    Smartphone,
    BrainCircuit,
    Check,
    ArrowUpRight,
} from "lucide-react";
import { services } from "@/data/landing";
import { fadeUp, staggerContainer, staggerItem } from "@/lib/motion";
import Tilt from "@/components/landing/effects/Tilt";

const iconMap = { Zap, Layers, Smartphone, BrainCircuit };

const Services = () => {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="section py-24 md:py-32 bg-[#070708]/70"
        >
            <div className="absolute inset-0 bg-grid-fine opacity-50 pointer-events-none" />
            <motion.div
                className="orb"
                style={{
                    width: 500,
                    height: 500,
                    top: "20%",
                    right: "-200px",
                    background:
                        "radial-gradient(circle, rgba(124,92,255,0.28), transparent 60%)",
                }}
                animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div
                    {...fadeUp}
                    className="flex flex-col items-start md:items-end md:flex-row md:justify-between gap-6 mb-14"
                >
                    <div className="max-w-2xl">
                        <span className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300">
                            02 / Services
                        </span>
                        <h2
                            data-testid="services-heading"
                            className="mt-4 font-display font-semibold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                        >
                            Software, shipped with{" "}
                            <span className="text-grad-cyan">conviction.</span>
                        </h2>
                    </div>
                    <p className="text-zinc-400 text-base max-w-md">
                        Beyond our own products, we partner with founders and enterprises
                        on four high-impact engagements.
                    </p>
                </motion.div>

                <motion.div
                    variants={staggerContainer}
                    initial="initial"
                    whileInView="whileInView"
                    viewport={{ once: true, margin: "-80px" }}
                    className="grid lg:grid-cols-2 gap-6"
                >
                    {services.map((svc, idx) => {
                        const Icon = iconMap[svc.icon] || Layers;
                        return (
                            <motion.div key={svc.id} variants={staggerItem}>
                                <Tilt max={6} scale={1.015} className="h-full">
                                    <div
                                        data-testid={`service-card-${svc.id}`}
                                        className="group relative rounded-2xl overflow-hidden glass glass-hover p-8 md:p-10 h-full flex flex-col"
                                    >
                                        {/* animated gradient border */}
                                        <div
                                            className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition duration-500"
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, rgba(0,229,255,0.12), rgba(124,92,255,0.06) 50%, transparent 80%)",
                                            }}
                                        />
                                        <div className="relative z-10 flex items-start justify-between gap-4">
                                            <motion.div
                                                whileHover={{ rotate: 10, scale: 1.1 }}
                                                className="h-14 w-14 rounded-xl bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 grid place-items-center"
                                            >
                                                <Icon
                                                    className="h-6 w-6 text-cyan-300"
                                                    strokeWidth={1.75}
                                                />
                                            </motion.div>
                                            <span className="font-mono text-xs text-zinc-600">
                                                0{idx + 1}
                                            </span>
                                        </div>

                                        <h3 className="relative z-10 mt-7 font-display font-semibold text-white text-2xl md:text-[28px] leading-tight">
                                            {svc.title}
                                        </h3>
                                        <p className="relative z-10 mt-2 text-cyan-300 font-mono text-xs uppercase tracking-[0.2em]">
                                            {svc.tagline}
                                        </p>
                                        <p className="relative z-10 mt-4 text-zinc-400 text-[15px] leading-relaxed">
                                            {svc.description}
                                        </p>

                                        <ul className="relative z-10 mt-6 space-y-2.5">
                                            {svc.bullets.map((b) => (
                                                <li
                                                    key={b}
                                                    className="flex items-start gap-3 text-sm text-zinc-300"
                                                >
                                                    <span className="mt-0.5 h-5 w-5 rounded-md bg-cyan-400/10 border border-cyan-400/30 grid place-items-center shrink-0">
                                                        <Check
                                                            className="h-3 w-3 text-cyan-300"
                                                            strokeWidth={3}
                                                        />
                                                    </span>
                                                    <span>{b}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <a
                                            href="#contact"
                                            data-testid={`service-cta-${svc.id}`}
                                            className="relative z-10 mt-8 inline-flex items-center gap-2 self-start text-sm font-semibold text-white group/cta"
                                        >
                                            <span className="border-b border-white/30 group-hover/cta:border-cyan-400 pb-0.5 transition-colors">
                                                Discuss your project
                                            </span>
                                            <ArrowUpRight className="h-4 w-4 group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5 transition-transform" />
                                        </a>
                                    </div>
                                </Tilt>
                            </motion.div>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
};

export default Services;
